<script lang="ts">
	/**
	 * `/Transactions/Subscription/:uuid` — one subscription, from the admin side (§13, §9.4, §9.6).
	 *
	 * The row on `/Transactions` says what it is; this says what can be done about it. Everything
	 * an admin can actually do to a subscription is here, and nothing that only looks like it can:
	 *
	 * - **End it now** (§9.4), which takes access the customer has paid for, so it carries a
	 *   mandatory note and the refund is a **separate** decision and a separate call — whether they
	 *   get their money back is a judgement the person doing it has to make explicitly.
	 * - **Refund an invoice** (§9.6), from the invoice panel, because a refund is issued against an
	 *   invoice and never against a subscription.
	 * - **Retry a refund** whose provider call never answered, on the same row under the same
	 *   idempotency key — so the answer to "did that refund go through" is never "issue another".
	 *
	 * What is deliberately **not** here is an admin-initiated plan or seat change. Those move money
	 * through the proration and SCA paths, and the customer-facing flow is the one place that is
	 * quoted, confirmed and idempotency-keyed; a second entry point into it would be a second place
	 * to get `subchange:{changeUUID}` wrong. An admin who needs it ends this subscription and the
	 * customer buys the tier they want.
	 */

	import { onMount } from 'svelte';
	import {
		Breadcrumb,
		Button,
		Table,
		TableBody,
		TableBodyCell,
		TableBodyRow,
		TableHead,
		TableHeadCell
	} from 'flowbite-svelte';
	import {
		AcceptedTerms,
		SelectedOptions,
		EmptyState,
		PageHeader,
		Panel,
		PromptButton,
		RefundCreditNoteCell,
		RoutedBreadcrumbItem,
		RoutedLink,
		SkeletonWrapper,
		SubscriptionStatusBadge,
		TextPlaceholder
	} from '../../../components';
	import SaleInvoices from './SaleInvoices.svelte';
	import SubscriptionDiagnostics from './SubscriptionDiagnostics.svelte';
	import { cancelSubscriptionNow, getAdminSubscription, retryAdminRefund } from '../../../AppNetwork';
	import { availableCurrencies, formatAmount, subscriptionPriceLine } from '../../../stores/storefront';
	import { formatCalendarDate } from '../../../Utilities';
	import { toastError, toastSuccess } from '../../../stores/toast';
	import type { AdminSubscriptionDetail } from '../../../AppTypes';

	export let params: { uuid?: string } = {};

	let detail: AdminSubscriptionDetail | null = null;
	let loading: boolean = true;
	let loadError: string = '';
	/** Names the action in flight, so only the control that started it shows as busy. */
	let busy: string = '';

	$: uuid = params.uuid ?? '';

	async function fnLoad(subscriptionUUID: string): Promise<void> {
		if (subscriptionUUID.length === 0) return;

		loading = true;
		loadError = '';
		try {
			detail = await getAdminSubscription(subscriptionUUID);
		} catch (error) {
			loadError = (error as Error).message;
		} finally {
			loading = false;
		}
	}

	onMount(() => void fnLoad(uuid));

	/**
	 * Ends it now rather than at period end (§9.4).
	 *
	 * The note is mandatory and recorded in the audit trail: taking access somebody has paid for
	 * with no stated reason is indistinguishable from a mistake six months later.
	 */
	async function fnCancelNow(note: string): Promise<void> {
		busy = 'cancel';
		try {
			await cancelSubscriptionNow(uuid, note);
			toastSuccess('Subscription ended.', 'Any refund is a separate decision.');
			await fnLoad(uuid);
		} catch (error) {
			toastError('The subscription was not ended.', (error as Error).message);
		} finally {
			busy = '';
		}
	}

	async function fnRetryRefund(refundUUID: string): Promise<void> {
		busy = refundUUID;
		try {
			await retryAdminRefund(refundUUID);
			toastSuccess('Refund re-submitted.');
			await fnLoad(uuid);
		} catch (error) {
			toastError('The refund was not re-submitted.', (error as Error).message);
		} finally {
			busy = '';
		}
	}

	/** A subscription that has already ended has nothing left to end. */
	$: canEnd =
		detail !== null && !['canceled', 'expired'].includes(detail.subscription.status);
</script>

<Breadcrumb class="mb-4">
	<RoutedBreadcrumbItem href="/" home>Overview</RoutedBreadcrumbItem>
	<RoutedBreadcrumbItem href="/Transactions">Transactions</RoutedBreadcrumbItem>
	<RoutedBreadcrumbItem>{detail?.subscription.productName ?? 'Subscription'}</RoutedBreadcrumbItem>
</Breadcrumb>

<PageHeader title={detail?.subscription.productName ?? 'Subscription'}>
	<svelte:fragment slot="description">
		One subscription, and everything that can be done to it.
	</svelte:fragment>
</PageHeader>

{#if loading}
	<SkeletonWrapper>
		<div class="widget bg-depth"><TextPlaceholder /></div>
	</SkeletonWrapper>
{:else if loadError.length > 0}
	<EmptyState variant="error" title="The subscription could not be loaded." description={loadError}>
		<Button color="alternative" size="sm" on:click={() => void fnLoad(uuid)}>Try again</Button>
	</EmptyState>
{:else if detail !== null}
	<div class="space-y-6">
		<Panel title="Subscription" faIcon="rotate">
			<svelte:fragment slot="titleRight">
				<SubscriptionStatusBadge
					status={detail.subscription.status}
					cancelAtPeriodEnd={detail.subscription.cancelAtPeriodEnd}
				/>
			</svelte:fragment>

			<dl class="grid gap-x-6 gap-y-3 sm:grid-cols-2 text-sm">
				<div>
					<dt class="card-label mb-1">Customer</dt>
					<dd>
						<!-- The answer to "whose", and a way to get there. -->
						<RoutedLink
							to={`/Customers/${detail.customer.userUUID}`}
							class="text-primary-500 hover:underline"
						>
							{detail.customer.billingName ?? detail.customer.displayName ?? detail.customer.email}
						</RoutedLink>
						{#if detail.customer.isFlagged}
							<!-- A chargeback flag is the first thing that changes what an admin should do
							     next, so it travels with the name rather than sitting in a panel below. -->
							<span class="ml-2 text-xs font-semibold text-red-600 dark:text-red-400">
								<i class="fa-solid fa-flag mr-1" />Chargeback flagged
							</span>
						{/if}
					</dd>
				</div>
				<div>
					<dt class="card-label mb-1">Billing</dt>
					<!-- Through the shared helper, so a per-seat plan states its total rather than a
					     unit price nowhere near what the customer pays (§9.7). -->
					<dd class="tabular-nums">{$subscriptionPriceLine(detail.subscription)}</dd>
				</div>
				<div>
					<dt class="card-label mb-1">Current period ends</dt>
					<dd>
						{detail.subscription.currentPeriodEnd === null
							? '—'
							: formatCalendarDate(detail.subscription.currentPeriodEnd)}
					</dd>
				</div>
				<div>
					<dt class="card-label mb-1">Started</dt>
					<dd>{formatCalendarDate(detail.subscription.createdAt)}</dd>
				</div>
				{#if detail.subscription.trialEnd !== null}
					<div>
						<dt class="card-label mb-1">Trial ends</dt>
						<dd>{formatCalendarDate(detail.subscription.trialEnd)}</dd>
					</div>
				{/if}
				{#if detail.subscription.graceUntil !== null}
					<div>
						<!-- Grace is access (§7.1). An admin looking at a failing payment needs the
						     deadline, not just the fact that something is wrong. -->
						<dt class="card-label mb-1">Grace ends</dt>
						<dd class="text-yellow-600 dark:text-yellow-400">
							{formatCalendarDate(detail.subscription.graceUntil)}
						</dd>
					</div>
				{/if}
				{#if detail.subscription.pendingChange !== null}
					<div class="sm:col-span-2">
						<dt class="card-label mb-1">Scheduled change</dt>
						<dd>
							To {detail.subscription.pendingChange.productName} on
							{formatCalendarDate(detail.subscription.pendingChange.effectiveAt)}
						</dd>
					</div>
				{/if}
				{#if detail.subscription.coupon !== null}
					<div class="sm:col-span-2">
						<dt class="card-label mb-1">Coupon</dt>
						<dd class="font-mono text-xs">{detail.subscription.coupon.code}</dd>
					</div>
				{/if}
			</dl>

			<div class="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-zinc-700">
				{#if canEnd}
					<!--
						Immediate, not at period end: it takes access the customer has paid for, so it
						needs a stated reason and a person behind it (§9.4). The refund is a separate
						decision and a separate call — the invoice panel below.
					-->
					<PromptButton
						color="alternative"
						size="sm"
						icon="ban"
						disabled={busy === 'cancel'}
						confirmText="End this subscription now?"
						confirmDetail="Access stops immediately, and any refund is a separate decision."
						confirmButtonText="End now"
						inputPlaceholder="Reason"
						on:confirm={event => void fnCancelNow(String(event.detail ?? ''))}
					>
						End now
					</PromptButton>
				{:else}
					<span class="text-sm text-default">This subscription has already ended.</span>
				{/if}
			</div>
		</Panel>

		<SaleInvoices
			invoices={detail.invoices}
			title="Invoices for this subscription"
			on:refunded={() => void fnLoad(uuid)}
		/>

		<!-- §4.7 — what was ticked before this was bought, in the words that were shown. -->
		<AcceptedTerms acceptances={detail.acceptedTerms} />

		<!--
			§4.8 — the options taken, and what each took off. Always rendered on a support screen,
			unlike the customer's own page: "were they given a discount for this" is a question
			support arrives with, and an absent panel cannot answer it either way.
		-->
		<SelectedOptions
			selections={detail.selectedOptions}
			currencies={$availableCurrencies}
			showKey />

		<Panel title="Refunds on this account" faIcon="money-bill-transfer">
			{#if detail.refunds.length === 0}
				<EmptyState compact title="No refunds." description="Nothing has gone back out." />
			{:else}
				<Table divClass="relative overflow-x-auto">
					<TableHead>
						<TableHeadCell>Amount</TableHeadCell>
						<TableHeadCell>Status</TableHeadCell>
						<TableHeadCell>Entitlement</TableHeadCell>
						<TableHeadCell>Note</TableHeadCell>
						<TableHeadCell>Credit note</TableHeadCell>
						<TableHeadCell />
					</TableHead>
					<TableBody>
						{#each detail.refunds as refund (refund.uuid)}
							<TableBodyRow>
								<TableBodyCell class="dataTable tabular-nums">
									{$formatAmount(refund.amount, refund.currency) ?? 'Amount unavailable'}
								</TableBodyCell>
								<TableBodyCell class="dataTable">{refund.status}</TableBodyCell>
								<TableBodyCell class="dataTable">
									{refund.revokedEntitlement ? 'Revoked' : 'Kept'}
								</TableBodyCell>
								<TableBodyCell class="dataTable">{refund.note ?? '—'}</TableBodyCell>
								<!-- §4.6. Three answers rather than two — see the component. -->
								<TableBodyCell class="dataTable">
									<RefundCreditNoteCell {refund} on:issued={() => void fnLoad(uuid)} />
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									<!--
										A refund with no provider id is money the customer is owed that never
										reached the provider. Re-submitting the **same row** under the same
										idempotency key is what makes "did that go through?" answerable
										without moving money twice.
									-->
									{#if refund.providerRefundID === null}
										<Button
											size="xs"
											color="alternative"
											disabled={busy === refund.uuid}
											on:click={() => void fnRetryRefund(refund.uuid)}
										>
											Retry
										</Button>
									{/if}
								</TableBodyCell>
							</TableBodyRow>
						{/each}
					</TableBody>
				</Table>
			{/if}
		</Panel>

		<Panel title="What this customer holds" faIcon="key">
			{#if detail.entitlements.length === 0}
				<EmptyState compact title="No entitlements." />
			{:else}
				<ul class="space-y-2">
					{#each detail.entitlements as entitlement (entitlement.entitlementKey)}
						<li class="widget bg-depth flex flex-wrap items-center gap-3 text-sm">
							<span class="font-mono text-xs grow">{entitlement.entitlementKey}</span>
							<span class="text-default">{entitlement.source}</span>
							<!-- Words, never colour alone: `grace` is access, and only the words say so. -->
							<span class="font-semibold">{entitlement.status}</span>
						</li>
					{/each}
				</ul>
			{/if}
		</Panel>

		<!-- Last, because it is read when something has gone wrong rather than every visit. Keyed on the
		     uuid so moving to another subscription loads that one's logs rather than keeping these. -->
		{#key detail.subscription.uuid}
			<SubscriptionDiagnostics uuid={detail.subscription.uuid} />
		{/key}
	</div>
{/if}
